/**
 * CitationsList — collapsed-by-default disclosure for the citation
 * chips that follow each assistant message.
 *
 * Sprint AI-MODULE v2.1 follow-up. Replaces the always-expanded
 * citations row that was crowding the chat surface — particularly
 * the bubble, where 12+ chips per turn dominate the panel. Default
 * state shows "N sources" with a chevron; click to expand inline.
 *
 * Dedupes by ``${kind}:${source_ref}`` so the same upstream file
 * cited twice in a turn only takes one slot.
 */
import { useMemo, useState, type FC } from 'react';
import { ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import type { CitationRef } from '../api/aiApi';
import CitationChip from './CitationChip';

interface Props {
  citations: CitationRef[];
  /** Force open on mount. Default is collapsed. */
  defaultOpen?: boolean;
}

const CitationsList: FC<Props> = ({ citations, defaultOpen = false }) => {
  const deduped = useMemo(() => {
    const seen = new Set<string>();
    return citations.filter((c) => {
      const key = `${c.kind}:${c.source_ref}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [citations]);

  const [open, setOpen] = useState(defaultOpen);
  if (!deduped.length) return null;

  return (
    <div className={'ai-citations' + (open ? ' is-open' : '')}>
      <button
        type="button"
        className="ai-citations__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Link2 size={11} />
        <span>{deduped.length} {deduped.length === 1 ? 'source' : 'sources'}</span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {open && (
        <div className="ai-citations-row">
          {deduped.map((c) => (
            <CitationChip key={c.id} citation={c} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CitationsList;
